import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUploader } from "@utils";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { Upload, Palette, ImagePlus, Sparkles, ArrowRight } from "lucide-react";

export default function DesignSystemOnboarding({ onBrandExtracted, onSkip, onLogoChange, logoUrl }) {
    const { t } = useTranslation();
    const brandRef = useRef(null);
    const logoRef = useRef(null);

    const { call: analyzeImage, isLoading: analyzing } = useBluelagoon({ method: "analyzeDesignFromImage" });

    const { uploadFiles: uploadBrand, loading: brandUploading } = useUploader({
        isPublic: true,
        onFileUpload: async ({ permanentUrl, key }) => {
            const res = await analyzeImage({ imageUrl: permanentUrl || key });
            const config = res?.data?.config || res?.config;
            if (config) {
                onBrandExtracted(config);
                toast.success(t("onboarding.designOnboarding.extractSuccess"));
            } else {
                toast.error(res?.data?.error || res?.error || "Extraction failed");
            }
        },
    });

    const { uploadFiles: uploadLogo, loading: logoUploading } = useUploader({
        isPublic: true,
        onFileUpload: ({ permanentUrl, key }) => onLogoChange(permanentUrl || key),
    });

    const isLoading = brandUploading || analyzing;

    return (
        <div className="max-w-lg mx-auto py-4 space-y-8">
            <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-2">
                    <Sparkles className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-xl font-bold">{t("onboarding.designOnboarding.title")}</h2>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">{t("onboarding.designOnboarding.subtitle")}</p>
            </div>

            {/* Step 1: Logo */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">1</span>
                    <span className="text-sm font-semibold">{t("onboarding.designOnboarding.uploadLogo")}</span>
                </div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo([e.target.files[0]])} />
                <button
                    onClick={() => logoRef.current?.click()}
                    disabled={logoUploading}
                    className="w-full flex items-center gap-4 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary hover:bg-primary/5 transition-all"
                >
                    {logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="w-14 h-14 object-contain rounded-lg border bg-white p-1" />
                    ) : (
                        <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <ImagePlus className="w-6 h-6 text-gray-400" />
                        </div>
                    )}
                    <div className="text-left flex-1">
                        <p className="text-sm font-medium">{logoUrl ? t("onboarding.designOnboarding.changeLogo") : t("onboarding.designOnboarding.dragDrop")}</p>
                        <p className="text-xs text-muted-foreground">{t("onboarding.designOnboarding.logoFormats")}</p>
                    </div>
                    {logoUploading && <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
                </button>
            </div>

            {/* Step 2: Colors & typography */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">2</span>
                    <span className="text-sm font-semibold">{t("onboarding.designOnboarding.chooseColors")}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input ref={brandRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadBrand([e.target.files[0]])} />
                    <button
                        onClick={() => brandRef.current?.click()}
                        disabled={isLoading}
                        className="flex items-start gap-3 p-4 border-2 rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left"
                    >
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Upload className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium">{t("onboarding.designOnboarding.uploadBrandBook")}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {isLoading ? t("onboarding.designOnboarding.extracting") : t("onboarding.designOnboarding.uploadBrandBookDescription")}
                            </p>
                        </div>
                        {isLoading && <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />}
                    </button>
                    <button
                        onClick={onSkip}
                        className="flex items-start gap-3 p-4 border-2 rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left"
                    >
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                            <Palette className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium">{t("onboarding.designOnboarding.skipToTemplates")}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding.designOnboarding.skipToTemplatesDescription")}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                    </button>
                </div>
            </div>
        </div>
    );
}
